import { Link } from "react-router-dom";
import {
  Syringe, Hospital, UtensilsCrossed, Lightbulb, HeartHandshake, ArrowRight,
  CalendarClock, Sandwich, Sun, MessagesSquare,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

const PILLARS = [
  {
    icon: Syringe,
    title: "Le rythme des perfusions",
    text: "Chaque semaine, la perfusion vient compenser les protéines perdues. À l'hôpital ou à domicile selon les situations, elle devient un rendez-vous de vie : on s'y prépare, on l'apprivoise, et on organise la semaine autour.",
    tips: ["Prévoir une tenue confortable et de quoi s'occuper", "Noter ses ressentis dans un carnet de suivi", "Prévenir l'équipe soignante en cas de fièvre ou de fatigue inhabituelle"],
    id: "pillar-perfusions",
  },
  {
    icon: Hospital,
    title: "Le suivi hospitalier hebdomadaire",
    text: "Consultations, bilans sanguins, ajustement des doses : le suivi rapproché est le pilier de la stabilité. Les équipes des hôpitaux Cochin et Beaujon connaissent bien la maladie et accompagnent les patients au long cours.",
    tips: ["Regrouper les rendez-vous le même jour quand c'est possible", "Garder ses ordonnances et résultats dans un même classeur", "Ne pas hésiter à poser toutes ses questions, même celles qui semblent petites"],
    id: "pillar-suivi",
  },
  {
    icon: UtensilsCrossed,
    title: "L'alimentation adaptée",
    text: "Un régime pauvre en graisses à longue chaîne est souvent recommandé : ces graisses sollicitent les vaisseaux lymphatiques. Les triglycérides à chaîne moyenne (TCM), mieux absorbés, permettent de cuisiner sans se priver.",
    tips: ["Privilégier les cuissons vapeur et les protéines maigres", "Demander un suivi avec un(e) diététicien(ne)", "Utiliser les huiles TCM sur conseil médical"],
    id: "pillar-alimentation",
  },
];

const MORAL = [
  { icon: MessagesSquare, title: "En parler", text: "Rompre le silence avec la famille, les amis, l'école ou l'employeur allège le poids de la maladie." },
  { icon: HeartHandshake, title: "Rencontrer d'autres malades", text: "Rien ne remplace le regard de quelqu'un qui comprend vraiment. L'association organise ces rencontres." },
  { icon: Sun, title: "Préserver ses joies", text: "Loisirs, sorties, projets : la maladie rythme la semaine, elle ne doit pas la définir." },
];

const VivreAvec = () => (
  <div data-testid="vivre-avec-page">
    <PageHero
      eyebrow="Le quotidien"
      title="Vivre avec la maladie de Waldmann"
      description="Perfusions hebdomadaires, suivi rapproché, alimentation adaptée : le quotidien demande de l'organisation. Voici nos repères et conseils pratiques, nourris de l'expérience des malades et de leurs familles."
      testid="vivre-avec-title"
    />

    <section className="py-16 md:py-24">
      <div className="container-site max-w-5xl space-y-14">
        {PILLARS.map((p, i) => (
          <Reveal key={p.id}>
            <article data-testid={p.id} className={`card-site grid gap-8 !p-7 sm:!p-10 lg:grid-cols-[1fr_0.85fr] ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
              <div className="[direction:ltr]">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EFF6FF] text-[#0284C7]">
                  <p.icon size={23} />
                </span>
                <h2 className="mt-5 font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2C59]">
                  {p.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{p.text}</p>
              </div>
              <div className="[direction:ltr] rounded-2xl bg-[#F8FAFC] p-6">
                <p className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.14em] text-[#0284C7]">
                  <Lightbulb size={16} /> Conseils pratiques
                </p>
                <ul className="mt-4 space-y-3">
                  {p.tips.map((t, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm sm:text-base font-medium text-slate-700">
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#FF6B6B]" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-[#EFF6FF] py-16 md:py-24">
      <div className="container-site">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <img
              src={IMAGES.doctorTalking}
              alt="Médecin échangeant chaleureusement avec une patiente"
              className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-xl shadow-[#0F2C59]/10"
              loading="lazy"
              data-testid="vivre-avec-image"
            />
          </Reveal>
          <Reveal delay={0.12}>
            <span className="eyebrow">Le moral compte aussi</span>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F2C59] leading-tight">
              Le soutien moral fait partie du traitement
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Une maladie chronique rare, c'est aussi de l'inquiétude, de la fatigue morale et
              parfois du sentiment d'isolement. Prendre soin du moral n'est pas un luxe : c'est
              une force pour tenir le cap, semaine après semaine.
            </p>
            <div className="mt-8 space-y-4">
              {MORAL.map((m, i) => (
                <div key={i} className="flex items-start gap-4 rounded-2xl bg-white p-5 shadow-sm" data-testid={`moral-${i}`}>
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#ECFDF5] text-[#10B981]">
                    <m.icon size={19} />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-[#0F2C59]">{m.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{m.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-16 md:py-24">
      <div className="container-site">
        <Reveal>
          <div className="grid gap-6 rounded-[2rem] bg-[#0F2C59] p-8 sm:p-12 sm:grid-cols-3">
            {[
              { icon: CalendarClock, title: "Une semaine type", text: "Perfusion le mercredi matin, bilan mensuel, vie de famille et projets tout autour : beaucoup de malades trouvent leur équilibre." },
              { icon: Sandwich, title: "Manger reste un plaisir", text: "Avec les bons réflexes et l'aide d'un diététicien, l'assiette reste gourmande malgré le régime adapté." },
              { icon: HeartHandshake, title: "Vous n'êtes pas seul(e)", text: "Notre ligne d'écoute et nos rencontres entre malades sont là à chaque étape, du diagnostic au quotidien." },
            ].map((c, i) => (
              <div key={i} data-testid={`daily-life-${i}`}>
                <c.icon size={26} className="text-[#7DD3FC]" />
                <h3 className="mt-4 font-heading text-lg font-bold text-white">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-sky-100/80">{c.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal className="mt-10 text-center">
          <p className="text-base font-semibold text-slate-600">
            Besoin d'en parler, d'un conseil ou d'une oreille attentive ?
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="vivre-avec-contact-button" className="btn-don">
              Contactez-nous <ArrowRight size={17} />
            </Link>
            <a href="tel:+33954243446" data-testid="vivre-avec-call-button" className="btn-outline-navy">
              09 54 24 34 46
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default VivreAvec;
